import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
    getAuthorizationToken,
    setAuthorizationToken,
    removeAuthorizationToken,
} from './auth-token';
import compressGraphql from './compress-graphql';

const GRAPHQL_URL = process.env.GRAPHQL_URL;
const HOST_URL = process.env.HOST_URL;

const getHeaders = () => {
    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };
    const authorizationToken = getAuthorizationToken();

    if (authorizationToken) {
        headers.authorization = `Bearer ${authorizationToken}`;
    }

    return headers;
};

async function apiCall({
    host = HOST_URL,
    url,
    method,
    headers = {},
    body,
}) {
    const response = await fetch(`${host}${url}`, {
        headers: { ...getHeaders(), ...headers },
        method,
        body: JSON.stringify(body),
    });

    if (response.status === 200) {
        return await response.json();
    }

    Sentry.captureException(String(response));

    throw response;
}

async function graphQLCall(url, request) {
    const response = await fetch(url, request);

    if (response.status === 200) {
        const json = await response.json();
        const { token } = response.headers;

        if (json.errors) {
            if (json.errors[0].extensions?.category === 'graphql-authorization' && getAuthorizationToken()) {
                await fetch(url,{
                    headers: getHeaders(),
                    method: 'POST',
                    body: JSON.stringify({ query: compressGraphql(`mutation { revokeCustomerToken { result }`) })
                });
                removeAuthorizationToken();
                document.location.reload();
            }

            Sentry.captureException(String(json.errors));

            throw json.errors;
        }

        if (token) {
            setAuthorizationToken(token);
        }

        return json.data;
    }

    Sentry.captureException(String(response));

    throw response;
}

const fetchGetQuery = ({ query, url = GRAPHQL_URL }) => (
    graphQLCall(`${url}?query=${compressGraphql(query)}`, {
        headers: getHeaders(),
        method: 'GET',
    })
);

const fetchPostQuery = ({ query, variables, url = GRAPHQL_URL, headers }) => (
    graphQLCall(url, {
        headers: { ...getHeaders(), ...headers },
        method: 'POST',
        body: JSON.stringify({ query: compressGraphql(query), variables })
    })
);

const getValueFromQuery = (query, values = []) => {
    values.forEach(value => {
        query = query[value];
    });

    return query;
};

const useQuery = ({
    data = null,
    isLoading = false,
    errors = null,
    ...startProps
} = {}) => {
    const [state, setState] = useState({ data, isLoading, errors });

    const runQuery = props => {
        const mergedProps = { ...startProps, ...props };
        const {
            query,
            variables,
            queryKey,
            url,
            isMethodPost,
        } = mergedProps;
        const actionFunction = isMethodPost ? fetchPostQuery : fetchGetQuery;

        setState({ ...state, isLoading: true });

        actionFunction({ query, variables, url })
            .then(response => {
                setState({
                    data: getValueFromQuery(response, queryKey),
                    isLoading: false,
                    errors: null,
                });
            }).catch(errors => {
                setState({
                    ...state,
                    isLoading: false,
                    errors: errors,
                });
            })
    };

    const setValues = values => setState({ ...state, ...values });

    return { ...state, runQuery, setValues };
};

const useQueryPromise = ({
    data = null,
    isLoading = false,
    ...startProps
} = {}) => {
    const [state, setState] = useState({ data, isLoading });

    const runQueryPromise = props => {
        const mergedProps = { ...startProps, ...props };
        const {
            query,
            variables,
            queryKey,
            url,
            isMethodPost,
        } = mergedProps;
        const actionFunction = isMethodPost ? fetchPostQuery : fetchGetQuery;

        setState({ ...state, isLoading: true });

        return actionFunction({ query, variables, url })
            .then(response => {
                setState({
                    data: getValueFromQuery(response, queryKey),
                    isLoading: false,
                });

                return response;
            }).catch(errors => {
                setState({
                    ...state,
                    isLoading: false,
                });

                throw errors;
            })
    };

    const setValues = values => setState({ ...state, ...values });

    return { ...state, runQueryPromise, setValues };
};

export {
    useQuery,
    useQueryPromise,
    fetchGetQuery,
    fetchPostQuery,
    apiCall,
}
